// src/pages/AdminUsers.js
import React, { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../context/AuthContext'
import { authFetch }   from '../utils/api'
import {
  FaKey,
  FaTrash,
  FaUser,
  FaUserEdit,
  FaChevronDown,
  FaChevronUp,
  FaSignOutAlt,
  FaCog,
  FaFileCsv
} from 'react-icons/fa'
import './AdminUsers.css'

const ROLE_OPTIONS = ['super', 'admin', 'standard']
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' }
]
const PAGE_SIZE = 20

function Badge({ children, type }) {
  const color = type === 'super' ? '#2563eb'
    : type === 'admin' ? '#10b981'
    : type === 'standard' ? '#64748b'
    : type === 'active' ? '#22c55e'
    : type === 'disabled' ? '#ef4444'
    : type === 'pending' ? '#a3a3a3'
    : '#e5e7eb'
  return <span className="badge" style={{ background: color + '22', color }}>{children}</span>
}

function ProfileDropdown({ user, onLogout }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="profile-dropdown">
      <button className="profile-btn" onClick={() => setOpen(o => !o)}>
        <FaUser style={{ marginRight: 6 }} /> {user?.name || 'Profile'} <FaChevronDown style={{ marginLeft: 4 }} />
      </button>
      {open && (
        <div className="profile-menu">
          <div className="profile-menu-item">View Profile</div>
          <div className="profile-menu-item">Settings</div>
          <div className="profile-menu-item" onClick={onLogout}><FaSignOutAlt style={{ marginRight: 6 }} />Logout</div>
        </div>
      )}
    </div>
  )
}

export default function AdminUsers() {
  const { token, login, user, logout, impersonate: impersonateUser } = useContext(AuthContext)
  const [users, setUsers]                 = useState([])
  const [filtered, setFiltered]           = useState([])
  const [searchTerm, setSearchTerm]       = useState('')
  const [filters, setFilters]             = useState({ role: '', department: '', status: '' })
  const [sortConfig, setSortConfig]       = useState({ key: '', direction: 'asc' })
  const [selected, setSelected]           = useState(new Set())
  const [showEditModal, setShowEditModal] = useState(false)
  const [editUser, setEditUser]           = useState(null)
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)
  const [page, setPage]                   = useState(1)
  const [showBulkBar, setShowBulkBar]     = useState(false)
  const [teamOptions, setTeamOptions]     = useState([])
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordResetData, setPasswordResetData] = useState(null)
  const [showDepartmentModal, setShowDepartmentModal] = useState(false)
  const [editDepartment, setEditDepartment] = useState(null)

  // Copy to clipboard function
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text)
      alert(`✅ ${label} copied to clipboard!`)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert(`✅ ${label} copied to clipboard!`)
    }
  }

  // Store teams list for department management
  const [teamsList, setTeamsList] = useState([])

  // Fetch teams for department dropdown
  const fetchTeams = async () => {
    try {
      const res = await authFetch('/api/teams', token)
      if (!res.ok) throw new Error('Failed to fetch teams')
      const body = await res.json()
      const list = Array.isArray(body)
        ? body
        : Array.isArray(body.teams)
          ? body.teams
          : []
      setTeamOptions(list.map(t => t.name))
      setTeamsList(list)
      return list
    } catch (err) {
      console.error('Error fetching teams:', err)
      setTeamOptions([])
      setTeamsList([])
      return []
    }
  }

  const fetchTeamsList = async () => {
    return await fetchTeams()
  }

  useEffect(() => {
    if (token) fetchTeams()
  }, [token])

  useEffect(() => {
    if (!token) return
    setLoading(true)
    ;(async function loadUsers() {
      try {
        const res  = await authFetch('/api/users', token)
        if (!res.ok) throw new Error(`Error ${res.status}`)
        const body = await res.json()
        const arr  = body.users ?? body
        setUsers(arr)
        setFiltered(arr)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  async function fetchUsers() {
    setLoading(true)
    try {
      const res  = await authFetch('/api/users', token)
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const body = await res.json()
      const arr  = body.users ?? body
      setUsers(arr)
      setFiltered(arr)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Search + filter + sort
  useEffect(() => {
    let data = [...users]
    if (searchTerm) {
      const t = searchTerm.toLowerCase()
      data = data.filter(u =>
        u.name.toLowerCase().includes(t) ||
        u.email.toLowerCase().includes(t)
      )
    }
    if (filters.role)       data = data.filter(u => u.role === filters.role)
    if (filters.department) {
      // Case-insensitive department filter
      const deptFilter = filters.department.toLowerCase().trim()
      data = data.filter(u => {
        const userDept = (u.department || '').toLowerCase().trim()
        return userDept === deptFilter
      })
    }
    if (filters.status)     data = data.filter(u => (filters.status === 'active') === u.isActive)
    if (sortConfig.key) {
      data.sort((a, b) => {
        const A = a[sortConfig.key] ?? ''
        const B = b[sortConfig.key] ?? ''
        if (A < B) return sortConfig.direction === 'asc' ? -1 : 1
        if (A > B) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    setFiltered(data)
    setPage(1)
  }, [users, searchTerm, filters, sortConfig])

  // Pagination
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE)

  function handleSort(key) {
    let dir = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') dir = 'desc'
    setSortConfig({ key, direction: dir })
  }

  function toggleSelectAll() {
    if (selected.size === paged.length) {
      setSelected(new Set())
      setShowBulkBar(false)
    } else {
      setSelected(new Set(paged.map(u => u._id)))
      setShowBulkBar(true)
    }
  }
  function toggleSelect(id) {
    const s = new Set(selected)
    s.has(id) ? s.delete(id) : s.add(id)
    setSelected(s)
    setShowBulkBar(s.size > 0)
  }

  function openEdit(user = null) {
    if (user) {
      setEditUser({ ...user })
    } else {
      setEditUser({
        name:       '',
        email:      '',
        role:       'standard',
        department: '',
        isActive:   true
      })
    }
    setShowEditModal(true)
  }

  async function saveEdit() {
    const payload = {
      name:       editUser.name,
      email:      editUser.email,
      role:       editUser.role,
      department: editUser.department,
      isActive:   editUser.isActive
    }
    try {
      if (editUser._id) {
        await authFetch(
          `/api/users/${editUser._id}`,
          token,
          {
            method:  'PUT',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload)
          }
        )
      } else {
        const res = await authFetch(
          '/api/users',
          token,
          {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload)
          }
        )
        // The server emails a single-use "set your password" link; no password is sent from here
        const created = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(created.message || created.error || 'Failed to create user')
        if (created.invitation && !created.invitation.emailSent && created.invitation.setupLink) {
          window.prompt(
            'The invitation email could not be sent. Share this single-use link with the user securely (valid 72 hours):',
            created.invitation.setupLink
          )
        } else {
          alert(`Invitation sent to ${payload.email}. They will set their own password.`)
        }
      }
      setShowEditModal(false)
      fetchUsers()
    } catch (err) {
      alert(err.message)
    }
  }

  async function deleteUser(id) {
    if (!window.confirm('Delete this user?')) return
    await authFetch(`/api/users/${id}`, token, { method: 'DELETE' })
    fetchUsers()
    setSelected(s => { const ns = new Set(s); ns.delete(id); return ns })
  }

  async function resetPassword(id) {
    const user = users.find(u => u._id === id)
    if (!user) return alert('User not found')
    
    // Create a custom modal for password reset options
    const sendEmail = window.confirm(`⚠️ Reset password for ${user.name}?\n\nThis will:\n• Generate a temporary password\n• Force them to change it on next login\n• Log them out of all sessions\n\n📧 Click OK to send email to user automatically\n📧 Click Cancel to get password without sending email`)
    
    try {
      const res = await authFetch(
        `/api/users/${id}/admin-reset-password`,
        token,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sendEmail })
        }
      )
      
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Failed to reset password')
      }
      
      const result = await res.json()
      
      // Show password reset modal with copy functionality
      setPasswordResetData({
        ...result,
        emailRequested: sendEmail
      })
      setShowPasswordModal(true)
      
      // Refresh the users list
      fetchUsers()
      
    } catch (error) {
      console.error('❌ Password reset failed:', error)
      alert(`❌ Password reset failed: ${error.message}`)
    }
  }

  async function impersonate(id) {
    if (!window.confirm("⚠️ You are about to log in as this user. Continue?")) return
    
    try {
      console.log('🔄 Starting impersonation for user ID:', id);
      
      const res = await authFetch(
        `/api/auth/impersonate/${id}`,
        token,
        { method: 'POST' }
      )
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('❌ Impersonation failed:', errorData);
        alert(`Impersonation failed: ${errorData.error || 'Unknown error'}`);
        return;
      }
      
      const { token: t2 } = await res.json();
      console.log('✅ Impersonation successful, new token received');
      
      // Clear any existing analytics session to prevent conflicts
      localStorage.removeItem('analytics_session_id');
      sessionStorage.clear();
      
      // Use the impersonate function which bypasses the automatic refresh
      impersonateUser(t2);
      
      console.log('🔄 Redirecting to dashboard...');
      window.location.href = '/';
      
    } catch (error) {
      console.error('❌ Impersonation error:', error);
      alert(`Impersonation failed: ${error.message}`);
    }
  }

  async function bulkDelete() {
    if (!window.confirm(`Delete ${selected.size} users?`)) return
    for (let id of selected) {
      await authFetch(`/api/users/${id}`, token, { method: 'DELETE' })
    }
    fetchUsers()
    setSelected(new Set())
    setShowBulkBar(false)
  }
  async function bulkRoleChange(e) {
    const newRole = e.target.value
    if (!newRole) return
    for (let id of selected) {
      await authFetch(
        `/api/users/${id}`,
        token,
        {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ role: newRole })
        }
      )
    }
    fetchUsers()
    setSelected(new Set())
    setShowBulkBar(false)
  }

  function clearFilters() {
    setFilters({ role: '', department: '', status: '' })
    setSearchTerm('')
  }

  function exportCSV() {
    const header = ['Name','Email','Role','Department','Status','Last Login']
    const rows = filtered.map(u => [
      u.name, u.email, u.role, u.department, u.isActive ? 'Active' : 'Disabled', u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never Logged In'
    ])
    const csv = [header, ...rows].map(r => r.map(x => '"'+(x||'')+'"').join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'users.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const departments = Array.from(
    new Set(users.map(u => u.department).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

  function openDepartmentEdit(department = null) {
    if (department) {
      // Find the team object by name from teamsList
      const team = teamsList.find(t => t.name === department)
      if (team) {
        setEditDepartment({ _id: team._id, name: team.name })
      } else {
        setEditDepartment({ name: department })
      }
    } else {
      setEditDepartment({ name: '' })
    }
    setShowDepartmentModal(true)
  }

  async function saveDepartment() {
    if (!editDepartment.name || !editDepartment.name.trim()) {
      alert('Department name is required')
      return
    }
    try {
      if (editDepartment._id) {
        // Update existing team
        await authFetch(
          `/api/teams/${editDepartment._id}`,
          token,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: editDepartment.name.trim() })
          }
        )
      } else {
        // Check if team name already exists
        const existingTeams = await fetchTeamsList()
        const existingTeam = existingTeams.find(t => 
          t.name.toLowerCase() === editDepartment.name.trim().toLowerCase()
        )
        if (existingTeam) {
          alert('A department with this name already exists')
          return
        }
        // Create new team
        await authFetch(
          '/api/teams',
          token,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: editDepartment.name.trim() })
          }
        )
      }
      setShowDepartmentModal(false)
      await fetchTeamsList()
      // Refresh users to update department dropdown
      fetchUsers()
    } catch (err) {
      alert(err.message || 'Failed to save department')
    }
  }

  async function deleteDepartment() {
    if (!editDepartment._id) {
      // If no _id, try to find by name
      const teams = await fetchTeamsList()
      const team = teams.find(t => t.name === editDepartment.name)
      if (!team) {
        alert('Department not found')
        return
      }
      setEditDepartment(team)
    }
    
    if (!window.confirm(`Delete department "${editDepartment.name}"? This will remove it from all users who have this department assigned.`)) return
    
    try {
      await authFetch(`/api/teams/${editDepartment._id}`, token, { method: 'DELETE' })
      setShowDepartmentModal(false)
      await fetchTeamsList()
      fetchUsers()
    } catch (err) {
      alert(err.message || 'Failed to delete department')
    }
  }

  return (
    <div className="admin-users-outer">
      <div className="admin-users-card">
        <div className="admin-users-header-row">
          <h1>User Management</h1>
          <ProfileDropdown user={user} onLogout={logout} />
        </div>
        <div className="admin-users-controls">
          <div className="admin-users-toolbar">
            <input
              className="search-bar"
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              aria-label="Search users"
            />
            <select
              value={filters.role}
              onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
            >
              <option value="">All Roles</option>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              value={filters.department}
              onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
            >
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <button className="create-btn" onClick={() => openDepartmentEdit(null)}>+ New Department</button>
            <button className="create-btn prominent" onClick={() => openEdit(null)}>+ New User</button>
          </div>
        </div>
        {showBulkBar && selected.size > 0 && (
          <div className="bulk-bar">
            <span>{selected.size} user{selected.size !== 1 ? 's' : ''} selected</span>
            <button className="bulk-delete" onClick={bulkDelete}><FaTrash style={{ marginRight: 4 }} />Delete</button>
            <select className="bulk-role" onChange={bulkRoleChange} defaultValue="">
              <option value="">Change Role…</option>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
        <div className="admin-users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selected.size === paged.length && paged.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(u => (
                <tr key={u._id} className={!u.isActive ? 'disabled' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(u._id)}
                      onChange={() => toggleSelect(u._id)}
                    />
                  </td>
                  <td>{u.name}</td>
                  <td style={{ wordBreak: 'break-all' }}>{u.email}</td>
                  <td><Badge type={u.role}>{u.role}</Badge></td>
                  <td>{u.department || '—'}</td>
                  <td><Badge type={u.isActive ? 'active' : 'disabled'}>{u.isActive ? 'Active' : 'Disabled'}</Badge></td>
                  <td>
                    {u.lastLogin
                      ? <span title={new Date(u.lastLogin).toLocaleString()}>{new Date(u.lastLogin).toLocaleDateString()}</span>
                      : <span className="never-login">Never Logged In</span>
                    }
                  </td>
                  <td className="actions">
                    <button title="Edit this user"       onClick={() => openEdit(u)}><FaUserEdit color="#2563eb"/></button>
                    <button title="Delete this user"     onClick={() => deleteUser(u._id)}><FaTrash color="#ef4444"/></button>
                    <button title="Send password reset"  onClick={() => resetPassword(u._id)}><FaKey color="#3b82f6"/></button>
                    <button
                      title="Impersonate this user (you will be logged in as them)"
                      onClick={() => impersonate(u._id)}
                    ><FaUser color="#64748b"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination-bar">
          <button disabled={page === 1} onClick={() => setPage(p => p-1)}>&lt; Prev</button>
          <span>Page {page} of {totalPages || 1}</span>
          <button disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p+1)}>Next &gt;</button>
        </div>
      </div>
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editUser._id ? 'Edit User' : 'Create New User'}</h2>
            <label>
              Name
              <input
                type="text"
                value={editUser.name}
                onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={editUser.email}
                onChange={e => setEditUser(u => ({ ...u, email: e.target.value }))}
              />
            </label>
            <label>
              Role
              <select
                value={editUser.role}
                onChange={e => setEditUser(u => ({ ...u, role: e.target.value }))}
              >
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label>
              Department
              <select
                value={editUser.department}
                onChange={e => setEditUser(u => ({ ...u, department: e.target.value }))}
              >
                <option value="">Select a team…</option>
                {teamOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={editUser.isActive ? 'active' : 'disabled'}
                onChange={e => setEditUser(u => ({
                  ...u,
                  isActive: e.target.value === 'active'
                }))}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>

            <div className="modal-actions">
              <button onClick={() => setShowEditModal(false)}>Cancel</button>
              <button onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && passwordResetData && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <h2>🔐 Password Reset Successful</h2>
            
            <div style={{ backgroundColor: '#e8f5e8', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #4caf50' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#2e7d32' }}>✅ Password has been reset successfully!</p>
              <p style={{ margin: '0', fontSize: '14px', color: '#555' }}>
                {passwordResetData.emailRequested 
                  ? passwordResetData.emailSent 
                    ? '📧 Email sent to user automatically'
                    : '⚠️ Email failed to send - please share manually'
                  : '📋 Manual sharing requested'
                }
              </p>
            </div>

            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <h3 style={{ marginTop: '0', color: '#333' }}>User Information:</h3>
              <p><strong>Name:</strong> {passwordResetData.userName}</p>
              <p><strong>Email:</strong> {passwordResetData.userEmail}</p>
              
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Temporary Password:</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    value={passwordResetData.temporaryPassword || 'Sent to the user by email (not shown)'} 
                    readOnly 
                    style={{ 
                      flex: 1, 
                      padding: '8px 12px', 
                      border: '2px solid #007bff', 
                      borderRadius: '4px', 
                      fontFamily: 'monospace', 
                      fontSize: '16px',
                      backgroundColor: '#f0f8ff'
                    }}
                  />
                  <button 
                    disabled={!passwordResetData.temporaryPassword}
                    onClick={() => copyToClipboard(passwordResetData.temporaryPassword, 'Temporary Password')}
                    style={{ 
                      padding: '8px 16px', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Login URL:</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    value={passwordResetData.loginUrl} 
                    readOnly 
                    style={{ 
                      flex: 1, 
                      padding: '8px 12px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <button 
                    onClick={() => copyToClipboard(passwordResetData.loginUrl, 'Login URL')}
                    style={{ 
                      padding: '8px 16px', 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
              </div>
            </div>

            {passwordResetData.emailError && (
              <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffc107' }}>
                <p style={{ margin: '0', color: '#856404' }}>
                  <strong>📧 Email Error:</strong> {passwordResetData.emailError}
                </p>
              </div>
            )}

            <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffc107' }}>
              <p style={{ margin: '0', fontWeight: 'bold', color: '#856404' }}>⚠️ Important Instructions:</p>
              <ul style={{ marginBottom: '0', color: '#856404' }}>
                <li>Share these credentials securely with the user</li>
                <li>User will be forced to change password on next login</li>
                <li>Temporary password expires when user changes it</li>
              </ul>
            </div>

            <div className="modal-actions">
              <button 
                onClick={() => copyToClipboard(
                  `Login Details for ${passwordResetData.userName}:\n\nEmail: ${passwordResetData.userEmail}\nTemporary Password: ${passwordResetData.temporaryPassword}\nLogin URL: ${passwordResetData.loginUrl}\n\nPlease change your password immediately after login.`, 
                  'All Login Details'
                )}
                style={{ 
                  backgroundColor: '#6f42c1', 
                  color: 'white', 
                  marginRight: '10px'
                }}
              >
                📋 Copy All Details
              </button>
              <button onClick={() => setShowPasswordModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Department Management Modal */}
      {showDepartmentModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editDepartment?._id ? 'Edit Department' : 'Create New Department'}</h2>
            <label>
              Department Name
              <input
                type="text"
                value={editDepartment?.name || ''}
                onChange={e => setEditDepartment({ ...editDepartment, name: e.target.value })}
                placeholder="e.g., Sales, IT, HR"
              />
            </label>
            <div className="modal-actions">
              <button onClick={() => setShowDepartmentModal(false)}>Cancel</button>
              {editDepartment?._id && (
                <button 
                  onClick={deleteDepartment}
                  style={{ backgroundColor: '#ef4444', color: 'white', marginRight: '10px' }}
                >
                  Delete
                </button>
              )}
              <button onClick={saveDepartment}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
